import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { apiUrl } from './hooks/useOnboardingStatus';
import UsageDashboard from './UsageDashboard';
import './styles.css';

function SubscriptionInfo({ data }) {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const friendlyNumber = (value, decimals = 2) => {
    const num = Number(value || 0);
    return num.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const handleCancelSubscription = async () => {
    if (window.confirm('Are you sure you want to cancel your subscription?')) {
      try {
        const accessToken = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          },
        });

        const response = await fetch(apiUrl('/user/subscription/cancel'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          alert('Your subscription cancellation has been scheduled.');
          navigate('/');
        } else {
          console.error('Failed to cancel subscription');
          alert('An error occurred while cancelling your subscription.');
        }
      } catch (error) {
        console.error('Error cancelling subscription:', error);
        alert('An error occurred while cancelling your subscription.');
      }
    }
  };

  // Stripe is the source of truth for money actually owed: invoices, discounts,
  // credits, and the amount due below all come from the subscription data.
  // Never replace these with the estimated usage value from the dashboard.
  const billing = data?.billing || null;
  const discount = billing?.discount || null;
  const amountDue = Number(billing?.amountDue);
  const hasAmountDue =
    billing?.amountDue != null && Number.isFinite(amountDue);
  const complimentary =
    hasAmountDue &&
    amountDue === 0 &&
    discount?.complimentary === true;

  const formatCurrency = (amount, currency = 'USD') =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);

  const derivePlanTier = (value) => {
    if (!value || typeof value !== 'string') return null;
    const v = value.toLowerCase();
    if (v.includes('studio')) return 'Studio';
    if (v.includes('pro')) return 'Pro';
    if (v.includes('starter')) return 'Starter';
    return null;
  };

  const planDisplay = (
    derivePlanTier(data?.planName) ||
    derivePlanTier(data?.plan) ||
    null
  );

  return (
    <div className="subscription-container">
      <h3 className="subscription-title">Usage</h3>
      {planDisplay && (
        <div className="plan-badge" title={data?.status || ''}>
          {planDisplay}
        </div>
      )}
      {hasAmountDue && (
        <div
          className={`billing-summary ${complimentary ? 'complimentary' : ''}`}
          aria-label="Billing status"
        >
          <span className="billing-summary-label">Billing</span>
          <strong>
            {complimentary
              ? 'Complimentary access: $0 due'
              : `${formatCurrency(amountDue, billing?.currency)} due`}
          </strong>
          {discount?.active && !complimentary && discount?.percentOff != null && (
            <span className="billing-summary-detail">
              {friendlyNumber(discount.percentOff, 0)}% discount applied
            </span>
          )}
        </div>
      )}

      <UsageDashboard />

      <div className="subscription-actions">
        <button 
          className="subscription-button change-plan" 
          onClick={() => navigate('/pricing')}
        >
          Change Plan
        </button>
        <button 
          className="subscription-button cancel-plan" 
          onClick={handleCancelSubscription}
        >
          Cancel Subscription
        </button>
      </div>
    </div>
  );
}

export default SubscriptionInfo;
